import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import {
    getAllUserFormList, downloadUserForms
} from "../../Services/form_question";
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { Button, Flex, Tag, DatePicker } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import UserFormAnswerModal from "./UserFormAnswerModal"; // ✅ import modal
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import { fetchUsers } from "../../Services/user_question_history";
const { RangePicker } = DatePicker;


const columns = (showViewModal) => [
    {
        title: "Form ID",
        dataIndex: "id",
        key: "id",
    },
    {
        title: "User Name",
        dataIndex: ["user", "name"],
        key: "user.name",
    },
    {
        title: "Email",
        dataIndex: ["user", "email"],
        key: "user.email",
    },
    {
        title: "Submit From",
        dataIndex: "submit_from",
        key: "submit_from",
    },
    {
        title: "Subscription Plan Id",
        dataIndex: "user_subscription_plan_id",
        key: "user_subscription_plan_id",
        render: (text, record) =>
            record?.user_subscription_plan_id ? record.user_subscription_plan_id : "-",
    },
    // {
    //     title: "Plan Active",
    //     dataIndex: ["user_subscription_plan", "is_active"],
    //     key: "user_subscription_plan.is_active",
    //     render: (text, record) => (
    //         <Tag color={record?.user_subscription_plan.is_active ? "green" : "red"}>
    //             {record?.user_subscription_plan.is_active ? "Yes" : "No"}
    //         </Tag>
    //     ),
    // },
    {
        title: "Expiry Date",
        dataIndex: "expire_datetime",
        key: "expire_datetime",
        render: (text, record) =>
            record?.expire_datetime ? formatDate(record.expire_datetime) : "-",
    },
    {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (text, record) =>
            record?.created_at ? formatDate(record.created_at) : "-",
    },
    {
        title: "Updated At",
        dataIndex: "updated_at",
        key: "updated_at",
        render: (text, record) =>
            record?.updated_at ? formatDate(record.updated_at) : "-",
    },
    {
        title: "Actions",
        key: "actions",
        fixed: 'right',
        render: (_, record) => (
            <Flex gap="small">
                <Button type="link" icon={<EyeOutlined />} onClick={() => showViewModal(record)} />
            </Flex>
        ),
    },
];

function FormQuestionMaster() {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [dateRange, setDateRange] = useState({
            start_date: null,
            end_date: null,
        });

    const showViewModal = (record) => {
        setViewData(record);
        setViewModalVisible(true);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedUserId, dateRange]);

    const skip = (currentPage - 1) * pageSize;

    const { data, isLoading } = useQuery({
        queryKey: ["user_form_list", currentPage, pageSize, searchTerm, selectedUserId, dateRange],
        queryFn: () =>
            getAllUserFormList({
                skip,
                limit: pageSize,
                name: searchTerm,
                userId: selectedUserId,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
            }),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setDateRange({
                start_date: dates[0].format("YYYY-MM-DD"),
                end_date: dates[1].format("YYYY-MM-DD"),
            });
        } else {
            setDateRange({
                start_date: null,
                end_date: null,
            });
        }
    };

    const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
        mutationFn: async () => {
            return await downloadUserForms({
                user_id: selectedUserId,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
                search: searchTerm
            });
        },
    });

    return (
        <div>
            <Flex justify="space-between">
                <h3>User Form List</h3>
                <Button type="primary" onClick={triggerDownload} loading={isDownloading}>Export</Button>
            </Flex>
            <Flex gap="10px">
                <CustomFilterSelect
                    label="User"
                    fetchOptions={fetchUsers}
                    filterKey="user"
                    onChange={(key, value) => setSelectedUserId(value)}
                    renderOption={(option) => `#${option.value} ${option.label} (${option.email})`}
                    className="custom-user-select"
                />
                <DebouncedInput
                    placeholder="Search by Answer..."
                    value={searchTerm}
                    onChange={setSearchTerm}
                    delay={1000}
                />
                <div >
                    <RangePicker onChange={handleDateChange} disabledDate={(current) => current && current.toDate() > new Date()} />
                </div>
            </Flex>
            <br />
            <CustomTable
                columns={columns(showViewModal)}
                data={data?.data || []}
                loading={isLoading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleTableChange}
                maxHeight={"calc(90vh - 180px)"}
            />

            <UserFormAnswerModal
                visible={viewModalVisible}
                onClose={() => setViewModalVisible(false)}
                data={viewData}
            />
        </div>
    );
}

export default FormQuestionMaster;
